/**
 * The original-level media pool, client side.
 *
 * Media added on one tended card (an upload, a recording, a Pexels pick)
 * belongs to the ORIGINAL and is available to every tend of it. The server
 * resolves that on every read; this event lets the cards already on screen
 * learn about an addition immediately, without a refetch — handing a mounted
 * card a new payload re-runs its media sync and once lost in-session
 * recordings, so listeners APPEND to what they hold instead. Every listener
 * on the original hears every addition, the announcing card included: a
 * card mounted twice (the strip behind an item modal) keeps both copies in
 * step, and appending what is already held is a no-op.
 */

export const POOL_CHANGED_EVENT = 'tend:pool-changed';

export interface PoolChangedDetail {
  /** The original content item the asset now belongs to. */
  originalId: number;
  /** The asset's URL as the server stores it. */
  url: string;
  /** The tended item it was added on (informational). */
  fromItemId: number;
}

export const announcePoolChange = (originalId: number | null | undefined, url: string, fromItemId: number): void => {
  if (typeof window === 'undefined' || !originalId || !url) return;
  window.dispatchEvent(new CustomEvent<PoolChangedDetail>(POOL_CHANGED_EVENT, {
    detail: { originalId, url, fromItemId },
  }));
};

/** Subscribe for one original; the handler receives every addition (append if missing). */
export const onPoolChange = (
  originalId: number | null | undefined,
  handler: (url: string) => void,
): (() => void) => {
  if (typeof window === 'undefined' || !originalId) return () => {};
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<PoolChangedDetail>).detail;
    if (detail?.originalId === originalId && detail.url) handler(detail.url);
  };
  window.addEventListener(POOL_CHANGED_EVENT, listener);
  return () => window.removeEventListener(POOL_CHANGED_EVENT, listener);
};
